import { debounce } from 'es-toolkit';
import { useCallback, useEffect, useId } from 'react';
import { useForm } from 'react-hook-form';

import { numberUtils, stringHelper } from '@zg-rentals/rental-manager-env';

import { Box, FieldSet, FormField, FormHelp, IconButton, Input, Label, Paragraph } from '@zillow/constellation-10';
import { IconMinusCircleFilled } from '@zillow/constellation-icons';

import { flexContainerStyles, flexRowStyles } from '../../styles';

import type { RentalManagerRenterAddOrUpdateRenterDto } from '@zg-rentals/ts-rental-manager-api-client';

import { boxContainerStyles, titleRowStyles } from './styles';

const FIELD_NAME = {
  FIRST_NAME: 'firstName',
  LAST_NAME: 'lastName',
  RAW_EMAIL_ADDRESS: 'rawEmailAddress',
  PHONE_NUMBER: 'phoneNumber',
} as const;

interface IFormValues {
  [FIELD_NAME.FIRST_NAME]: string;
  [FIELD_NAME.LAST_NAME]: string;
  [FIELD_NAME.RAW_EMAIL_ADDRESS]: string;
  [FIELD_NAME.PHONE_NUMBER]: string;
}

export interface TenantFormProps {
  id: number;
  isEditMode?: boolean;
  title?: string;
  renter: RentalManagerRenterAddOrUpdateRenterDto;
  hasFieldError?: boolean;
  // action props
  onChange?: ({
    id,
    value,
    name,
    hasFieldError,
  }: {
    id: number;
    value: string;
    name: string;
    hasFieldError?: boolean;
  }) => void;
  onDeleteTenantClick?: (id: number) => void;
  onFieldError?: ({ id, hasFieldError }: { id: number; hasFieldError: boolean }) => void;
}

const ERROR_MESSAGE = {
  FIRST_NAME: {
    REQUIRED: 'Enter a first name',
    MAX_LENGTH: 'First name must be at most 35 characters',
    NO_NUMBERS: 'First name cannot contain numbers',
  },
  LAST_NAME: {
    REQUIRED: 'Enter a last name',
    MAX_LENGTH: 'Last name must be at most 35 characters',
    NO_NUMBERS: 'Last name cannot contain numbers',
  },
  EMAIL_ADDRESS: {
    REQUIRED: 'Enter a valid email address',
    MAX_LENGTH: 'Email address must be at most 254 characters',
    PATTERN: 'Enter a valid email address',
  },
  PHONE_NUMBER: {
    REQUIRED: 'Enter a valid phone number',
    MIN_LENGTH: 'Phone number must be at least 10 digits',
    MAX_LENGTH: 'Phone number must be at most 10 digits',
    PATTERN: 'Enter a valid phone number',
  },
  PATTERNS: {
    NO_LEADING_SPACES: 'No leading white spaces',
  },
};

// regular expression patterns
const NO_LEADING_SPACES_PATTERN = /^\S.*$/;
const NO_NUMBERS_PATTERN = /^[^0-9]*$/;

const TenantForm = ({
  id,
  isEditMode = false,
  title = 'Tenant',
  renter,
  onChange,
  onDeleteTenantClick,
  onFieldError,
}: TenantFormProps): JSX.Element => {
  const { firstName, lastName, rawEmailAddress, phoneNumber } = renter;

  const {
    clearErrors,
    formState: { errors, isValid, touchedFields, dirtyFields },
    getValues,
    register,
    setValue,
    trigger,
  } = useForm<IFormValues>({
    mode: 'onBlur',
    criteriaMode: 'firstError',
    defaultValues: {
      firstName,
      lastName,
      // transform rawEmailAddress to emailAddress
      rawEmailAddress,
      phoneNumber,
    },
  });

  useEffect(() => {
    // run validation on mount to show existing errors immediately, but only if the fields have initial values
    // This checks if any of the values are "falsy" (e.g., empty string, null, or undefined).
    // To specifically check for empty strings:
    const hasInitialValues = [firstName, lastName, rawEmailAddress, phoneNumber].some((val) => val !== '');

    if (hasInitialValues) {
      trigger();
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
  }, [trigger]);

  // listen for errors
  useEffect(() => {
    onFieldError?.({ id, hasFieldError: isValid === false });
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
  }, [isValid]);

  /**
   * Set values via auto-fill
   */
  const handleAutoFill = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.name as keyof IFormValues, e.target.value, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    clearErrors(e.target.name as keyof IFormValues);

    // set values via auto-fill
    handleAutoFill(e);

    // sync with parent renter data
    handleFieldChangeDebounce();
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleFieldChangeDebounce = useCallback(
    debounce(() => {
      const values = getValues();

      // sync parent tenant form list
      Object.keys(values).forEach((key) => {
        onChange?.({ id, value: values[key as keyof IFormValues], name: key });
      });
    }, 30),
    [id, onChange],
  );

  const titleId = useId();

  const handleDeleteTenantClick = () => {
    onDeleteTenantClick?.(id);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fieldName = FIELD_NAME.RAW_EMAIL_ADDRESS;
    const isFieldTouchedAndDirty = touchedFields[fieldName] && dirtyFields[fieldName];

    clearErrors(fieldName);

    // Only validate on change if field has already been touched (blurred) and dirty
    setValue(fieldName, e.target.value, {
      shouldValidate: isFieldTouchedAndDirty,
      shouldDirty: true,
      shouldTouch: false,
    });

    // sync with parent renter data
    handleFieldChangeDebounce();
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fieldName = FIELD_NAME.PHONE_NUMBER;
    const isFieldTouchedAndDirty = touchedFields[fieldName] && dirtyFields[fieldName];
    const formattedPhone = numberUtils.formatPhoneNumberInput(e.target.value);

    clearErrors(fieldName);

    // Only validate on change if field has already been touched (blurred) and dirty
    setValue(fieldName, formattedPhone, {
      shouldValidate: isFieldTouchedAndDirty,
      shouldDirty: true,
      shouldTouch: false,
    });

    // sync with parent renter data
    handleFieldChangeDebounce();
  };

  return (
    <Box data-testid="TenantForm" className={boxContainerStyles({ isEditMode })}>
      <Box className={titleRowStyles}>
        {isEditMode === false && (
          <>
            <Paragraph id={titleId} textStyle="body-bold" css={{ marginBottom: 'tight' }}>
              {title}
            </Paragraph>

            <IconButton
              tone="critical"
              emphasis="bare"
              size="sm"
              icon={<IconMinusCircleFilled />}
              title={`Remove ${title}`}
              onClick={handleDeleteTenantClick}
            />
          </>
        )}
      </Box>

      <FieldSet
        className={`${flexContainerStyles} fs-unmask`}
        aria-labelledby={isEditMode ? undefined : titleId}
        aria-label={isEditMode ? title : undefined}
      >
        <Box className={flexRowStyles} data-testid="TenantForm-FirstName">
          <FormField
            label={
              <Label size="md" required={true}>
                First name
              </Label>
            }
            error={!!errors.firstName?.message}
            description={errors.firstName && <FormHelp>{errors?.firstName?.message}</FormHelp>}
          >
            <Input
              {...register(FIELD_NAME.FIRST_NAME, {
                required: {
                  value: true,
                  message: ERROR_MESSAGE.FIRST_NAME.REQUIRED,
                },
                maxLength: {
                  value: 35,
                  message: ERROR_MESSAGE.FIRST_NAME.MAX_LENGTH,
                },
                pattern: {
                  value: NO_LEADING_SPACES_PATTERN,
                  message: ERROR_MESSAGE.PATTERNS.NO_LEADING_SPACES,
                },
                validate: {
                  noNumbers: (value) => NO_NUMBERS_PATTERN.test(value) || ERROR_MESSAGE.FIRST_NAME.NO_NUMBERS,
                },
              })}
              placeholder="Enter first name"
              size="md"
              name={FIELD_NAME.FIRST_NAME}
              onChange={handleChange}
            />
          </FormField>
        </Box>

        <Box className={flexRowStyles} data-testid="TenantForm-LastName">
          <FormField
            label={
              <Label size="md" required={true}>
                Last name
              </Label>
            }
            error={!!errors.lastName?.message}
            description={errors.lastName && <FormHelp>{errors?.lastName?.message}</FormHelp>}
          >
            <Input
              className="fs-mask"
              {...register(FIELD_NAME.LAST_NAME, {
                required: {
                  value: true,
                  message: ERROR_MESSAGE.LAST_NAME.REQUIRED,
                },
                maxLength: {
                  value: 35,
                  message: ERROR_MESSAGE.LAST_NAME.MAX_LENGTH,
                },
                pattern: {
                  value: NO_LEADING_SPACES_PATTERN,
                  message: ERROR_MESSAGE.PATTERNS.NO_LEADING_SPACES,
                },
                validate: {
                  noNumbers: (value) => NO_NUMBERS_PATTERN.test(value) || ERROR_MESSAGE.LAST_NAME.NO_NUMBERS,
                },
              })}
              placeholder="Enter last name"
              size="md"
              name={FIELD_NAME.LAST_NAME}
              onChange={handleChange}
            />
          </FormField>
        </Box>

        <Box className={flexRowStyles} data-testid="TenantForm-EmailAddress">
          <FormField
            label={
              <Label size="md" required={true}>
                Email address
              </Label>
            }
            error={!!errors.rawEmailAddress?.message}
            description={errors.rawEmailAddress && <FormHelp>{errors?.rawEmailAddress?.message}</FormHelp>}
          >
            <Input
              className="fs-mask"
              {...register(FIELD_NAME.RAW_EMAIL_ADDRESS, {
                required: {
                  value: true,
                  message: ERROR_MESSAGE.EMAIL_ADDRESS.REQUIRED,
                },
                maxLength: {
                  value: 254,
                  message: ERROR_MESSAGE.EMAIL_ADDRESS.MAX_LENGTH,
                },
                validate: {
                  validEmail: (value) => stringHelper.validateEmail(value) || ERROR_MESSAGE.EMAIL_ADDRESS.PATTERN,
                },
              })}
              placeholder="example@example.com"
              size="md"
              name={FIELD_NAME.RAW_EMAIL_ADDRESS}
              onChange={handleEmailChange}
            />
          </FormField>
        </Box>

        <Box className={flexRowStyles} data-testid="TenantForm-PhoneNumber">
          <FormField
            label={
              <Label size="md" required={true}>
                Phone number
              </Label>
            }
            error={!!errors.phoneNumber?.message}
            description={errors.phoneNumber && <FormHelp>{errors?.phoneNumber?.message}</FormHelp>}
          >
            <Input
              className="fs-mask"
              {...register(FIELD_NAME.PHONE_NUMBER, {
                required: {
                  value: true,
                  message: ERROR_MESSAGE.PHONE_NUMBER.REQUIRED,
                },
                maxLength: {
                  value: 14,
                  message: ERROR_MESSAGE.PHONE_NUMBER.MAX_LENGTH,
                },
                pattern: {
                  value: /^(?:\+?\d{1,4}?[-.\s]?)?(?:\(\d{3}\)|\d{3})(?:[-.\s]?\d{3})?[-.\s]?\d{4,6}$/,
                  message: ERROR_MESSAGE.PHONE_NUMBER.PATTERN,
                },
                minLength: {
                  value: 14,
                  message: ERROR_MESSAGE.PHONE_NUMBER.MIN_LENGTH,
                },
              })}
              placeholder="(123) 456-7890"
              size="md"
              name={FIELD_NAME.PHONE_NUMBER}
              onChange={handlePhoneChange}
            />
          </FormField>
        </Box>
      </FieldSet>
    </Box>
  );
};

export default TenantForm;
